import { useTranslation } from "react-i18next";
import { usePortalLogo } from "@/hooks/use-portal-logo";
import { monogram } from "@/lib/initials";

/** `header` sits in the signed-in header row; `entry` heads the pre-auth pages. */
export type BrandMarkSize = "header" | "entry";

interface BrandMarkProps {
  orgName: string;
  /** The utility's public slug; null where the page has none. */
  slug: string | null;
  hasLogo: boolean;
  size?: BrandMarkSize;
}

/*
 * Each size fixes the mark's height and nothing else: the logo's box is at
 * least as wide as it is tall, so the monogram fits it while the logo loads,
 * and capped, so a wide logo leaves the name beside it room to ellipsize. A
 * square logo takes only its own width.
 */
const SIZE: Record<BrandMarkSize, { box: string; tile: string; logo: string }> = {
  header: {
    box: "h-[30px] min-w-[30px] max-w-[120px] lg:h-9 lg:min-w-9 lg:max-w-40",
    tile: "size-[30px] rounded-[8px] text-2xs lg:size-9 lg:rounded-[9px] lg:text-xs",
    logo: "h-[30px] lg:h-9",
  },
  entry: {
    box: "h-12 min-w-12 max-w-[200px] lg:h-14 lg:min-w-14 lg:max-w-60",
    tile: "size-12 rounded-[12px] text-base lg:size-14 lg:rounded-[14px] lg:text-lg",
    logo: "h-12 lg:h-14",
  },
};

/** The decorative monogram; the org name printed beside it names the utility. */
function MonogramTile({ orgName, size }: { orgName: string; size: BrandMarkSize }) {
  return (
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center bg-brand font-semibold tracking-[0.02em] text-brand-ink ${SIZE[size].tile}`}
    >
      {monogram(orgName)}
    </span>
  );
}

/**
 * The utility's mark at a fixed height (30px below `lg` and 36px from `lg` in
 * the header, 48px and 56px on the entry pages): its logo when the public logo
 * read answers, otherwise a monogram of its name on the brand fill. A utility
 * with a logo holds one box from the first paint, the monogram in it until the
 * logo arrives, so the swap never moves the row's height. A logo is drawn for
 * white paper, so on dark it sits on a white plate.
 */
export function BrandMark({ orgName, slug, hasLogo, size = "header" }: BrandMarkProps) {
  const { t } = useTranslation("customer");
  const logoUrl = usePortalLogo(slug, hasLogo);

  if (!hasLogo || !slug) return <MonogramTile orgName={orgName} size={size} />;

  return (
    <span className={`flex shrink-0 items-center ${SIZE[size].box}`}>
      {logoUrl ? (
        <span className="inline-flex max-w-full bg-logo-plate dark:rounded-[8px] dark:p-1.5">
          <img
            src={logoUrl}
            alt={t("shell.logoAlt", { org: orgName })}
            className={`block w-auto max-w-full object-contain object-left ${SIZE[size].logo}`}
          />
        </span>
      ) : (
        <MonogramTile orgName={orgName} size={size} />
      )}
    </span>
  );
}
